<template>
  <div>
    <v-row v-if="!$store.getters.arena">
      <v-col cols="12" class="pt-0">
        <v-alert type="info">
          <span class="mb-0 text--white">{{
            $t('entities.finance.chooseArena')
          }}</span>
        </v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="6">
        <v-row align="center">
          <v-col cols="auto" class="py-0 pr-0">
            <v-tooltip color="transparent" top>
              <template v-slot:activator="{ on }">
                <v-btn
                  @click="refresh"
                  v-on="on"
                  :disabled="loading"
                  class="glow mr-2"
                  color="primary"
                  x-small
                  fab>
                  <v-icon> refresh</v-icon>
                </v-btn>
              </template>
              <v-card class="elevation-2 px-2 py-1">
                <small>{{ $t('refreshData') }}</small>
              </v-card>
            </v-tooltip>
          </v-col>
          <v-col cols="10" class="py-0 pl-2">
            <p class="mt-2 mb-0">{{ $t('generated') }}: {{ dateString }}</p>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <!-- Shop V2 Key Metrics -->
    <v-row>
      <v-col cols="12" sm="6">
        <CardStat
          :loading="loadingShopV2"
          :value="`${todaysIncomeShopV2}`"
          :text="$t('entities.finance.todaysIncome')" />
      </v-col>
      <v-col cols="12" sm="6">
        <CardStat
          :loading="loadingShopV2"
          :value="`${monthlyIncomeShopV2}`"
          :text="`${dateShopV2} ${$t('entities.finance.monthlyIncome')}`"
          color="orange" />
      </v-col>
    </v-row>
    <!-- Shop V2 Daily Finance -->
    <v-row class="mt-4">
      <v-col cols="12">
        <v-card class="elevation-2">
          <DataTableSkeletonLoader :loading="initialLoadShopV2" />
          <v-data-table
            v-show="!initialLoadShopV2"
            :headers="dailyFinanceHeadersShopV2"
            :items="dailyFinanceShopV2"
            :dense="$vuetify.breakpoint.xs && !loadingShopV2"
            :loading="loadingShopV2"
            :loading-text="$t('components.noData.fetchingEntries')"
            :options.sync="dailyFinanceOptionsShopV2"
            :server-items-length="dailyFinancePaginationShopV2.total"
            :items-per-page="dailyFinancePaginationShopV2.per_page"
            :header-props="{
              sortIcon: 'arrow_circle_up',
              sortByText: $t('miscellaneous.sortBy'),
            }"
            :class="{ 'mobile-empty-datatable': $vuetify.breakpoint.xsOnly }"
            hide-default-footer>
            <template v-slot:top>
              <v-toolbar class="mb-10 pt-6" flat>
                <v-toolbar-title
                  >{{ $t('entities.finance.dailyFinance') }}
                </v-toolbar-title>
                <v-divider class="mx-4" inset vertical></v-divider>
                <small>{{
                  addThousandSeparator(dailyFinancePaginationShopV2.total)
                }}</small>
                <v-spacer />
                <!-- Desktop -->
                <template
                  v-if="$store.getters.arena && $vuetify.breakpoint.smAndUp">
                  <v-col cols="auto" class="text-center mt-10">
                    <v-dialog
                      ref="dialogShopV2"
                      v-model="modalShopV2"
                      :return-value.sync="dateShopV2"
                      persistent
                      width="290px">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="dateShopV2"
                          v-bind="attrs"
                          v-on="on"
                          :label="$t('entities.finance.chooseMonth')"
                          prepend-icon="mdi-calendar"
                          readonly></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="dateShopV2"
                        :locale="$i18n.locale"
                        :max="new Date().toISOString().substr(0, 7)"
                        type="month"
                        scrollable>
                        <v-spacer></v-spacer>
                        <v-btn
                          text
                          color="primary"
                          @click="modalShopV2 = false">
                          {{ $t('miscellaneous.cancel') }}
                        </v-btn>
                        <v-btn text color="primary" @click="saveShopV2($event)">
                          {{ $t('miscellaneous.ok') }}
                        </v-btn>
                      </v-date-picker>
                    </v-dialog>
                  </v-col>
                  <v-col cols="auto" class="text-center">
                    <v-menu offset-y>
                      <template v-slot:activator="{ on: menuOn, attrs }">
                        <v-tooltip color="transparent" top>
                          <template v-slot:activator="{ on: tooltipOn }">
                            <v-btn
                              v-bind="attrs"
                              v-on="{ ...menuOn, ...tooltipOn }"
                              class="glow ml-2"
                              color="primary"
                              :disabled="
                                loadingFinancialReportShopV2 &&
                                loadingIndexesShopV2.includes(dateShopV2)
                              "
                              depressed
                              x-small
                              fab>
                              <template
                                v-if="
                                  loadingFinancialReportShopV2 &&
                                  loadingIndexesShopV2.includes(dateShopV2)
                                ">
                                <ProgressCircular color="primary" />
                              </template>
                              <template v-else>
                                <v-icon color="white" small>
                                  mdi-file-pdf-box
                                </v-icon>
                              </template>
                            </v-btn>
                          </template>
                          <v-card class="elevation-2 px-4 py-2">
                            <span>{{
                              $t('entities.finance.generatePdf')
                            }}</span>
                          </v-card>
                        </v-tooltip>
                      </template>
                      <v-list dense>
                        <v-list-item
                          @click="
                            generateReportShopV2(
                              'monthly',
                              null,
                              'pdf',
                              'business',
                            )
                          ">
                          <v-list-item-title>
                            {{ $t('entities.finance.dayType.business') }}
                          </v-list-item-title>
                        </v-list-item>
                        <v-list-item
                          @click="
                            generateReportShopV2(
                              'monthly',
                              null,
                              'pdf',
                              'calendar',
                            )
                          ">
                          <v-list-item-title>
                            {{ $t('entities.finance.dayType.calendar') }}
                          </v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                    <v-menu offset-y>
                      <template v-slot:activator="{ on: menuOn, attrs }">
                        <v-tooltip color="transparent" top>
                          <template v-slot:activator="{ on: tooltipOn }">
                            <v-btn
                              v-bind="attrs"
                              v-on="{ ...menuOn, ...tooltipOn }"
                              class="glow ml-2"
                              color="primary"
                              :disabled="
                                loadingFinancialReportShopV2 &&
                                loadingIndexesShopV2.includes(dateShopV2)
                              "
                              depressed
                              x-small
                              fab>
                              <template
                                v-if="
                                  loadingFinancialReportShopV2 &&
                                  loadingIndexesShopV2.includes(dateShopV2)
                                ">
                                <ProgressCircular color="primary" />
                              </template>
                              <template v-else>
                                <v-icon color="white" small>
                                  mdi-file-excel-box
                                </v-icon>
                              </template>
                            </v-btn>
                          </template>
                          <v-card class="elevation-2 px-4 py-2">
                            <span>{{
                              $t('entities.finance.generateCSV')
                            }}</span>
                          </v-card>
                        </v-tooltip>
                      </template>
                      <v-list dense>
                        <v-list-item
                          @click="
                            generateReportShopV2(
                              'monthly',
                              null,
                              'csv',
                              'business',
                            )
                          ">
                          <v-list-item-title>
                            {{ $t('entities.finance.dayType.business') }}
                          </v-list-item-title>
                        </v-list-item>
                        <v-list-item
                          @click="
                            generateReportShopV2(
                              'monthly',
                              null,
                              'csv',
                              'calendar',
                            )
                          ">
                          <v-list-item-title>
                            {{ $t('entities.finance.dayType.calendar') }}
                          </v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </v-col>
                </template>
              </v-toolbar>
              <!-- Mobile -->
              <v-row
                v-if="$store.getters.arena && $vuetify.breakpoint.xs"
                justify="center"
                align="center">
                <v-col cols="7" class="text-center pl-6">
                  <v-dialog
                    ref="dialogShopV2Mobile"
                    v-model="modalShopV2"
                    :return-value.sync="dateShopV2"
                    persistent
                    width="290px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="dateShopV2"
                        v-bind="attrs"
                        v-on="on"
                        :label="$t('entities.finance.chooseMonth')"
                        prepend-icon="mdi-calendar"
                        readonly></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="dateShopV2"
                      :locale="$i18n.locale"
                      type="month"
                      scrollable>
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="modalShopV2 = false">
                        {{ $t('miscellaneous.cancel') }}
                      </v-btn>
                      <v-btn text color="primary" @click="saveShopV2($event)">
                        {{ $t('miscellaneous.ok') }}
                      </v-btn>
                    </v-date-picker>
                  </v-dialog>
                </v-col>
                <v-col cols="5" class="text-center">
                  <v-menu offset-y>
                    <template v-slot:activator="{ on: menuOn, attrs }">
                      <v-tooltip color="transparent" top>
                        <template v-slot:activator="{ on: tooltipOn }">
                          <v-btn
                            v-bind="attrs"
                            v-on="{ ...menuOn, ...tooltipOn }"
                            class="glow ml-2"
                            color="primary"
                            :disabled="
                              loadingFinancialReportShopV2 &&
                              loadingIndexesShopV2.includes(dateShopV2)
                            "
                            depressed
                            x-small
                            fab>
                            <template
                              v-if="
                                loadingFinancialReportShopV2 &&
                                loadingIndexesShopV2.includes(dateShopV2)
                              ">
                              <ProgressCircular color="primary" />
                            </template>
                            <template v-else>
                              <v-icon color="white" small>
                                mdi-file-pdf-box
                              </v-icon>
                            </template>
                          </v-btn>
                        </template>
                        <v-card class="elevation-2 px-4 py-2">
                          <span>{{ $t('entities.finance.generatePdf') }}</span>
                        </v-card>
                      </v-tooltip>
                    </template>
                    <v-list dense>
                      <v-list-item
                        @click="
                          generateReportShopV2(
                            'monthly',
                            null,
                            'pdf',
                            'business',
                          )
                        ">
                        <v-list-item-title>
                          {{ $t('entities.finance.dayType.business') }}
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        @click="
                          generateReportShopV2(
                            'monthly',
                            null,
                            'pdf',
                            'calendar',
                          )
                        ">
                        <v-list-item-title>
                          {{ $t('entities.finance.dayType.calendar') }}
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                  <v-menu offset-y>
                    <template v-slot:activator="{ on: menuOn, attrs }">
                      <v-tooltip color="transparent" top>
                        <template v-slot:activator="{ on: tooltipOn }">
                          <v-btn
                            v-bind="attrs"
                            v-on="{ ...menuOn, ...tooltipOn }"
                            class="glow ml-2"
                            color="primary"
                            :disabled="
                              loadingFinancialReportShopV2 &&
                              loadingIndexesShopV2.includes(dateShopV2)
                            "
                            depressed
                            x-small
                            fab>
                            <template
                              v-if="
                                loadingFinancialReportShopV2 &&
                                loadingIndexesShopV2.includes(dateShopV2)
                              ">
                              <ProgressCircular color="primary" />
                            </template>
                            <template v-else>
                              <v-icon color="white" small>
                                mdi-file-excel-box
                              </v-icon>
                            </template>
                          </v-btn>
                        </template>
                        <v-card class="elevation-2 px-4 py-2">
                          <span>{{ $t('entities.finance.generateCSV') }}</span>
                        </v-card>
                      </v-tooltip>
                    </template>
                    <v-list dense>
                      <v-list-item
                        @click="
                          generateReportShopV2(
                            'monthly',
                            null,
                            'csv',
                            'business',
                          )
                        ">
                        <v-list-item-title>
                          {{ $t('entities.finance.dayType.business') }}
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        @click="
                          generateReportShopV2(
                            'monthly',
                            null,
                            'csv',
                            'calendar',
                          )
                        ">
                        <v-list-item-title>
                          {{ $t('entities.finance.dayType.calendar') }}
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-col>
              </v-row>
            </template>
            <template v-slot:item.date="{ item }">
              {{ reformatDate(item.date) }} ({{
                formatDay(item.date).substring(0, 3)
              }})
            </template>
            <template v-slot:item.cash="{ item }">
              <span v-if="item.cash && item.cash !== '/'">
                <b>{{ item.cash }}</b>
              </span>
              <span v-else> / </span>
            </template>
            <template v-slot:item.credit_card="{ item }">
              <span v-if="item.credit_card && item.credit_card !== '/'">
                <b>{{ item.credit_card }}</b>
              </span>
              <span v-else> / </span>
            </template>
            <template v-slot:item.bank="{ item }">
              <span v-if="item.bank && item.bank !== '/'">
                <b>{{ item.bank }}</b>
              </span>
              <span v-else> / </span>
            </template>
            <template v-slot:item.manual_discount="{ item }">
              <span v-if="item.manual_discount && item.manual_discount !== '/'">
                <b>{{ item.manual_discount }}</b>
              </span>
              <span v-else> / </span>
            </template>
            <template v-slot:item.income="{ item }">
              <b>{{ item.income }}</b>
            </template>
            <template v-slot:item.action="{ item }">
              <v-menu offset-y>
                <template v-slot:activator="{ on: menuOn, attrs }">
                  <v-tooltip color="transparent" top>
                    <template v-slot:activator="{ on: tooltipOn }">
                      <v-btn
                        v-bind="attrs"
                        v-on="{ ...menuOn, ...tooltipOn }"
                        class="glow ml-2"
                        color="primary"
                        :disabled="
                          loadingFinancialReportShopV2 &&
                          loadingIndexesShopV2.includes(item.date)
                        "
                        depressed
                        x-small
                        fab>
                        <template
                          v-if="
                            loadingFinancialReportShopV2 &&
                            loadingIndexesShopV2.includes(item.date)
                          ">
                          <ProgressCircular color="primary" />
                        </template>
                        <template v-else>
                          <v-icon color="white" small>
                            mdi-file-pdf-box
                          </v-icon>
                        </template>
                      </v-btn>
                    </template>
                    <v-card class="elevation-2 px-4 py-2">
                      <span>{{ $t('entities.finance.generatePdf') }}</span>
                    </v-card>
                  </v-tooltip>
                </template>
                <v-list dense>
                  <v-list-item
                    @click="
                      generateReportShopV2(
                        'daily',
                        item.date,
                        'pdf',
                        'business',
                      )
                    ">
                    <v-list-item-title>
                      {{ $t('entities.finance.dayType.business') }}
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    @click="
                      generateReportShopV2(
                        'daily',
                        item.date,
                        'pdf',
                        'calendar',
                      )
                    ">
                    <v-list-item-title>
                      {{ $t('entities.finance.dayType.calendar') }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <v-menu offset-y>
                <template v-slot:activator="{ on: menuOn, attrs }">
                  <v-tooltip color="transparent" top>
                    <template v-slot:activator="{ on: tooltipOn }">
                      <v-btn
                        v-bind="attrs"
                        v-on="{ ...menuOn, ...tooltipOn }"
                        class="glow ml-2"
                        color="primary"
                        :disabled="
                          loadingFinancialReportShopV2 &&
                          loadingIndexesShopV2.includes(item.date)
                        "
                        depressed
                        x-small
                        fab>
                        <template
                          v-if="
                            loadingFinancialReportShopV2 &&
                            loadingIndexesShopV2.includes(item.date)
                          ">
                          <ProgressCircular color="primary" />
                        </template>
                        <template v-else>
                          <v-icon color="white" small>
                            mdi-file-excel-box
                          </v-icon>
                        </template>
                      </v-btn>
                    </template>
                    <v-card class="elevation-2 px-4 py-2">
                      <span>{{ $t('entities.finance.generateCSV') }}</span>
                    </v-card>
                  </v-tooltip>
                </template>
                <v-list dense>
                  <v-list-item
                    @click="
                      generateReportShopV2(
                        'daily',
                        item.date,
                        'csv',
                        'business',
                      )
                    ">
                    <v-list-item-title>
                      {{ $t('entities.finance.dayType.business') }}
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    @click="
                      generateReportShopV2(
                        'daily',
                        item.date,
                        'csv',
                        'calendar',
                      )
                    ">
                    <v-list-item-title>
                      {{ $t('entities.finance.dayType.calendar') }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
            <template v-slot:no-data>
              <v-row>
                <v-col class="my-4 text-center">
                  <p class="mb-0">
                    {{ $t('components.noData.noEntriesFound') }}
                  </p>
                  <v-btn
                    v-if="
                      $store.getters.arena &&
                      !dailyFinancePaginationShopV2.search.length
                    "
                    @click="fetchDailyFinanceShopV2()"
                    color="primary"
                    class="mt-3"
                    outlined>
                    {{ $t('components.noData.tryAgain') }}
                  </v-btn>
                </v-col>
              </v-row>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="showShopV1">
      <v-col cols="12" sm="6">
        <CardStat
          :loading="loading"
          :value="`${monthlyIncome}`"
          :text="`${date} ${$t('entities.finance.monthlyIncome')} V1`"
          color="orange" />
      </v-col>
    </v-row>
    <!-- Shop V1 Daily Finance (legacy data) -->
    <v-row v-if="showShopV1" class="mt-4">
      <v-col cols="12">
        <v-card class="elevation-2">
          <DataTableSkeletonLoader :loading="initialLoad" />
          <v-data-table
            v-show="!initialLoad"
            :headers="dailyFinanceHeaders"
            :items="dailyFinance"
            :dense="$vuetify.breakpoint.xs && !loading"
            :loading="loading"
            :loading-text="$t('components.noData.fetchingEntries')"
            :options.sync="dailyFinanceOptions"
            :server-items-length="dailyFinancePagination.total"
            :items-per-page="dailyFinancePagination.per_page"
            :header-props="{
              sortIcon: 'arrow_circle_up',
              sortByText: $t('miscellaneous.sortBy'),
            }"
            :class="{ 'mobile-empty-datatable': $vuetify.breakpoint.xsOnly }"
            hide-default-footer>
            <template v-slot:top>
              <v-toolbar class="mb-10 pt-6" flat>
                <v-toolbar-title
                  >{{ $t('entities.finance.dailyFinance') }} V1
                </v-toolbar-title>
                <v-divider class="mx-4" inset vertical></v-divider>
                <small>{{
                  addThousandSeparator(dailyFinancePagination.total)
                }}</small>
                <v-spacer />
                <!-- Desktop -->
                <template
                  v-if="$store.getters.arena && $vuetify.breakpoint.smAndUp">
                  <v-col cols="auto" class="text-center mt-10">
                    <v-dialog
                      ref="dialog"
                      v-model="modal"
                      :return-value.sync="date"
                      persistent
                      width="290px">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="date"
                          v-bind="attrs"
                          v-on="on"
                          :label="$t('entities.finance.chooseMonth')"
                          prepend-icon="mdi-calendar"
                          readonly></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="date"
                        :locale="$i18n.locale"
                        :max="new Date().toISOString().substr(0, 7)"
                        type="month"
                        scrollable>
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" @click="modal = false">
                          {{ $t('miscellaneous.cancel') }}
                        </v-btn>
                        <v-btn text color="primary" @click="save($event)">
                          {{ $t('miscellaneous.ok') }}
                        </v-btn>
                      </v-date-picker>
                    </v-dialog>
                  </v-col>
                  <v-col cols="auto" class="text-center">
                    <v-tooltip color="transparent" top>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          @click="generateReport('monthly', null, 'pdf')"
                          v-on="on"
                          class="glow ml-2"
                          color="primary"
                          :disabled="
                            loadingFinancialReport &&
                            loadingIndexes.includes(date)
                          "
                          depressed
                          x-small
                          fab>
                          <template
                            v-if="
                              loadingFinancialReport &&
                              loadingIndexes.includes(date)
                            ">
                            <ProgressCircular color="primary" />
                          </template>
                          <template v-else>
                            <v-icon color="white" small>
                              mdi-file-pdf-box
                            </v-icon>
                          </template>
                        </v-btn>
                      </template>
                      <v-card class="elevation-2 px-4 py-2">
                        <span>{{ $t('entities.finance.generatePdf') }}</span>
                      </v-card>
                    </v-tooltip>
                    <v-tooltip color="transparent" top>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          @click="generateReport('monthly', null, 'csv')"
                          v-on="on"
                          class="glow ml-2"
                          color="primary"
                          :disabled="
                            loadingFinancialReport &&
                            loadingIndexes.includes(date)
                          "
                          depressed
                          x-small
                          fab>
                          <template
                            v-if="
                              loadingFinancialReport &&
                              loadingIndexes.includes(date)
                            ">
                            <ProgressCircular color="primary" />
                          </template>
                          <template v-else>
                            <v-icon color="white" small>
                              mdi-file-excel-box
                            </v-icon>
                          </template>
                        </v-btn>
                      </template>
                      <v-card class="elevation-2 px-4 py-2">
                        <span>{{ $t('entities.finance.generateCSV') }}</span>
                      </v-card>
                    </v-tooltip>
                  </v-col>
                </template>
              </v-toolbar>
              <!-- Mobile -->
              <v-row
                v-if="$store.getters.arena && $vuetify.breakpoint.xs"
                justify="center"
                align="center">
                <v-col cols="7" class="text-center pl-6">
                  <v-dialog
                    ref="dialog"
                    v-model="modal"
                    :return-value.sync="date"
                    persistent
                    width="290px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="date"
                        v-bind="attrs"
                        v-on="on"
                        :label="$t('entities.finance.chooseMonth')"
                        prepend-icon="mdi-calendar"
                        readonly></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="date"
                      :locale="$i18n.locale"
                      type="month"
                      scrollable>
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="modal = false">
                        {{ $t('miscellaneous.cancel') }}
                      </v-btn>
                      <v-btn text color="primary" @click="save($event)">
                        {{ $t('miscellaneous.ok') }}
                      </v-btn>
                    </v-date-picker>
                  </v-dialog>
                </v-col>
                <v-col cols="5" class="text-center">
                  <v-tooltip color="transparent" top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        @click="generateReport('monthly', null, 'pdf')"
                        v-on="on"
                        class="glow ml-2"
                        color="primary"
                        :disabled="
                          loadingFinancialReport &&
                          loadingIndexes.includes(date)
                        "
                        depressed
                        x-small
                        fab>
                        <template
                          v-if="
                            loadingFinancialReport &&
                            loadingIndexes.includes(date)
                          ">
                          <ProgressCircular color="primary" />
                        </template>
                        <template v-else>
                          <v-icon color="white" small>
                            mdi-file-pdf-box
                          </v-icon>
                        </template>
                      </v-btn>
                    </template>
                    <v-card class="elevation-2 px-4 py-2">
                      <span>{{ $t('entities.finance.generatePdf') }}</span>
                    </v-card>
                  </v-tooltip>
                  <v-tooltip color="transparent" top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        @click="generateReport('monthly', null, 'csv')"
                        v-on="on"
                        class="glow ml-2"
                        color="primary"
                        :disabled="
                          loadingFinancialReport &&
                          loadingIndexes.includes(date)
                        "
                        depressed
                        x-small
                        fab>
                        <template
                          v-if="
                            loadingFinancialReport &&
                            loadingIndexes.includes(date)
                          ">
                          <ProgressCircular color="primary" />
                        </template>
                        <template v-else>
                          <v-icon color="white" small>
                            mdi-file-excel-box
                          </v-icon>
                        </template>
                      </v-btn>
                    </template>
                    <v-card class="elevation-2 px-4 py-2">
                      <span>{{ $t('entities.finance.generateCSV') }}</span>
                    </v-card>
                  </v-tooltip>
                </v-col>
              </v-row>
            </template>
            <template v-slot:item.date="{ item }">
              {{ reformatDate(item.date) }} ({{
                formatDay(item.date).substring(0, 3)
              }})
            </template>
            <template v-slot:item.cash="{ item }">
              <span v-if="$store.getters.arena && item.income['Cash']">
                <b>{{
                  format(
                    item.income['Cash'],
                    true,
                    $store.getters.arena.currency,
                  )
                }}</b>
              </span>
              <span v-else> / </span>
            </template>
            <template v-slot:item.credit_card="{ item }">
              <span v-if="$store.getters.arena && item.income['Credit Card']">
                <b>{{
                  format(
                    item.income['Credit Card'],
                    true,
                    $store.getters.arena.currency,
                  )
                }}</b>
              </span>
              <span v-else> / </span>
            </template>
            <template v-slot:item.bank="{ item }">
              <span v-if="$store.getters.arena && item.income['Bank']">
                <b>{{
                  format(
                    item.income['Bank'],
                    true,
                    $store.getters.arena.currency,
                  )
                }}</b>
              </span>
              <span v-else> / </span>
            </template>
            <template v-slot:item.income="{ item }">
              <span v-if="$store.getters.arena">
                <b>{{
                  format(
                    item.income['Total'],
                    true,
                    $store.getters.arena.currency,
                  )
                }}</b>
              </span>
              <span v-else> / </span>
            </template>
            <template v-slot:item.action="{ item }">
              <v-tooltip color="transparent" top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    @click="generateReport('daily', item.date, 'pdf')"
                    v-on="on"
                    class="glow ml-2"
                    color="primary"
                    :disabled="
                      loadingFinancialReport &&
                      loadingIndexes.includes(item.date)
                    "
                    depressed
                    x-small
                    fab>
                    <template
                      v-if="
                        loadingFinancialReport &&
                        loadingIndexes.includes(item.date)
                      ">
                      <ProgressCircular color="primary" />
                    </template>
                    <template v-else>
                      <v-icon color="white" small> mdi-file-pdf-box</v-icon>
                    </template>
                  </v-btn>
                </template>
                <v-card class="elevation-2 px-4 py-2">
                  <span>{{ $t('entities.finance.generatePdf') }}</span>
                </v-card>
              </v-tooltip>
              <v-tooltip color="transparent" top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    @click="generateReport('daily', item.date, 'csv')"
                    v-on="on"
                    class="glow ml-2"
                    color="primary"
                    :disabled="
                      loadingFinancialReport &&
                      loadingIndexes.includes(item.date)
                    "
                    depressed
                    x-small
                    fab>
                    <template
                      v-if="
                        loadingFinancialReport &&
                        loadingIndexes.includes(item.date)
                      ">
                      <ProgressCircular color="primary" />
                    </template>
                    <template v-else>
                      <v-icon color="white" small> mdi-file-excel-box</v-icon>
                    </template>
                  </v-btn>
                </template>
                <v-card class="elevation-2 px-4 py-2">
                  <span>{{ $t('entities.finance.generateCSV') }}</span>
                </v-card>
              </v-tooltip>
            </template>
            <template v-slot:no-data>
              <v-row>
                <v-col class="my-4 text-center">
                  <p class="mb-0">
                    {{ $t('components.noData.noEntriesFound') }}
                  </p>
                  <v-btn
                    v-if="
                      $store.getters.arena &&
                      !dailyFinancePagination.search.length
                    "
                    @click="fetchDailyFinance()"
                    color="primary"
                    class="mt-3"
                    outlined>
                    {{ $t('components.noData.tryAgain') }}
                  </v-btn>
                </v-col>
              </v-row>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Vue from 'vue'
import CardStat from '@/components/CardStat.vue'
import DataTableSkeletonLoader from '@/components/crud/DataTableSkeletonLoader.vue'
import ProgressCircular from '@/components/ProgressCircular.vue'
import DashboardApi from '@/services/main/DashboardApi'
import DailyFinance from '@/models/misc/DailyFinance'

import { axiosCatch } from '@/mixins/crud/methods/axios-catch.js'
import currency from '@/mixins/currency'
import convertDate from '@/mixins/convert-date'
import thousandSeparator from '@/mixins/thousand-separator'
import user from '@/mixins/crud/computed/user'
import convertCase from '@/mixins/convert-case'
import formatDate from '@/mixins/format-date'
import formatDay from '@/mixins/format-day'
import ReportApi from '@/services/shop/ReportApi'
import { arenaHasShopV1History } from '@/utils/shopV1'

export default Vue.extend({
  components: {
    CardStat,
    ProgressCircular,
    DataTableSkeletonLoader,
  },

  mixins: [
    convertDate,
    axiosCatch,
    currency,
    thousandSeparator,
    user,
    convertCase,
    formatDate,
    formatDay,
  ],

  data: () => ({
    dateString: '',
    initialLoad: true,
    loading: false,
    loadingFinancialReport: false,
    loadingIndexes: [],
    todaysIncome: 0,
    monthlyIncome: 0,
    paymentMethods: [],
    date: '2026-01',
    menu: false,
    modal: false,
    modalShopV2: false,
    dailyFinance: [],
    // Shop V2 separate state
    loadingShopV2: false,
    initialLoadShopV2: true,
    loadingFinancialReportShopV2: false,
    loadingIndexesShopV2: [],
    todaysIncomeShopV2: 0,
    monthlyIncomeShopV2: 0,
    paymentMethodsShopV2: [],
    dateShopV2: null,
    dailyFinanceShopV2: [],
    dailyFinancePaginationShopV2: {
      search: '',
      current_page: 1,
      from: 1,
      last_page: 0,
      path: '',
      per_page: 31,
      to: 0,
      total: 0,
    },
    dailyFinanceOptionsShopV2: {
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
      groupBy: [],
      groupDesc: [],
      mustSort: false,
      multiSort: false,
    },
    dailyFinancePagination: {
      search: '',
      current_page: 1,
      from: 1,
      last_page: 0,
      path: '',
      per_page: 31,
      to: 0,
      total: 0,
    },
    dailyFinanceOptions: {
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
      groupBy: [],
      groupDesc: [],
      mustSort: false,
      multiSort: false,
    },
  }),

  created() {
    // Shop v1 data is frozen — always show its last active month
    this.date = '2026-01'
    this.dateShopV2 = new Date().toISOString().substr(0, 7)
  },

  computed: {
    showShopV1() {
      const arena = this.$store.getters.arena
      return arenaHasShopV1History(arena ? arena.id : null)
    },
    dailyFinanceHeaders() {
      const headers = [
        {
          text: this.$t('miscellaneous.date'),
          align: 'left',
          value: 'date',
          sortable: false,
        },
        {
          text: this.$t('miscellaneous.income'),
          align: 'left',
          value: 'income',
          sortable: false,
        },
        {
          text: this.$t('miscellaneous.actions'),
          value: 'action',
          sortable: false,
        },
      ]

      let counter = 1
      for (const paymentMethod in this.paymentMethods) {
        headers.splice(counter, 0, {
          text: this.$t(
            `miscellaneous.paymentMethods.${this.titleToCamelCase(
              this.paymentMethods[paymentMethod],
            )}`,
          ),
          align: 'left',
          value: this.camelToSnakeCase(
            this.titleToCamelCase(this.paymentMethods[paymentMethod]),
          ),
          sortable: false,
        })
        counter++
      }

      return headers
    },
    dailyFinanceHeadersShopV2() {
      const headers = [
        {
          text: this.$t('miscellaneous.date'),
          align: 'left',
          value: 'date',
          sortable: false,
        },
        {
          text: this.$t('miscellaneous.manualDiscount'),
          align: 'left',
          value: 'manual_discount',
          sortable: false,
        },
        {
          text: this.$t('miscellaneous.income'),
          align: 'left',
          value: 'income',
          sortable: false,
        },
        {
          text: this.$t('miscellaneous.actions'),
          value: 'action',
          sortable: false,
        },
      ]

      const codeToI18nKey = {
        CSH: 'cash',
        CRD: 'creditCard',
        BNK: 'bank',
      }
      const codeToValue = {
        CSH: 'cash',
        CRD: 'credit_card',
        BNK: 'bank',
      }

      let counter = 1
      for (const code of this.paymentMethodsShopV2) {
        const i18nKey = codeToI18nKey[code]
        const value = codeToValue[code]
        if (i18nKey && value) {
          headers.splice(counter, 0, {
            text: this.$t(`miscellaneous.paymentMethods.${i18nKey}`),
            align: 'left',
            value,
            sortable: false,
          })
          counter++
        }
      }

      return headers
    },
  },

  mounted() {
    this.refresh()
  },

  methods: {
    async fetchDailyFinance(
      append = `&size=${this.dailyFinancePagination.per_page}&page=${this.dailyFinancePagination.current_page}&date=${this.date}-01`,
    ) {
      try {
        this.loading = true
        if (this.$store.getters.arena) {
          const response = await DashboardApi.finance(
            this.$store.getters.arena.id,
            append,
          )
          this.dailyFinance = Object.entries(
            response.data.daily_income.data,
          ).map(([date, DailyFinanceDto]) => {
            return {
              income: new DailyFinance(DailyFinanceDto),
              date: date,
            }
          })
          this.todaysIncome = this.format(
            response.data.todays_income,
            true,
            this.$store.getters.arena.currency,
          )
          this.monthlyIncome = this.format(
            response.data.monthly_income,
            true,
            this.$store.getters.arena.currency,
          )
          this.paymentMethods = response.data.payment_methods
          response.data.daily_income.per_page = parseInt(
            response.data.daily_income.per_page,
          )
          this.dailyFinancePagination.current_page =
            response.data.daily_income.current_page
          this.dailyFinancePagination.per_page =
            response.data.daily_income.per_page
          this.dailyFinancePagination.total = response.data.daily_income.total
          this.dailyFinancePagination.last_page =
            response.data.daily_income.last_page
          this.dailyFinancePagination.from = response.data.daily_income.from
          this.dailyFinancePagination.to = response.data.daily_income.to
        }
      } catch (e) {
        console.warn(`Finance API failed.`)
        console.log(e)
      } finally {
        this.initialLoad = false
        this.loading = false
      }
    },
    async fetchDailyFinanceShopV2() {
      const paymentMethodCodeToKey = {
        CSH: 'cash',
        CRD: 'credit_card',
        BNK: 'bank',
      }

      try {
        this.loadingShopV2 = true
        if (this.$store.getters.arena) {
          const [year, month] = this.dateShopV2.split('-').map(Number)
          const dateGte = `${year}-${String(month).padStart(2, '0')}-01`
          const nextMonth = month === 12 ? 1 : month + 1
          const nextYear = month === 12 ? year + 1 : year
          const dateLt = `${nextYear}-${String(nextMonth).padStart(2, '0')}-01`

          const response = await ReportApi.report(
            this.$store.getters.arena.id,
            dateGte,
            dateLt,
          )

          const now = new Date()
          const stepReports = response.data.step_reports.filter(
            (step) => new Date(step.date_gte) <= now,
          )
          const uniqueCodes = new Set()

          this.dailyFinanceShopV2 = stepReports
            .map((step) => {
              const report = step.report
              const date = step.date_gte.split('T')[0]
              const currencyCode = report.currency_code ?? ''

              const manualDiscount = report.manual_discount_gross_amount
              const item = {
                date,
                income: `${report.gross_amount} ${currencyCode}`,
                manual_discount:
                  manualDiscount != null
                    ? `${manualDiscount} ${currencyCode}`
                    : '/',
                cash: '/',
                credit_card: '/',
                bank: '/',
              }

              for (const pm of report.payment_method_incomes ?? []) {
                const key = paymentMethodCodeToKey[pm.payment_method_code]
                if (key) {
                  uniqueCodes.add(pm.payment_method_code)
                  item[key] = `${pm.gross_amount} ${currencyCode}`
                }
              }

              return item
            })
            .reverse()

          this.paymentMethodsShopV2 = [...uniqueCodes]
          this.dailyFinancePaginationShopV2.total = stepReports.length

          const totalReport = response.data.total_report
          const monthlyCurrency = totalReport.currency_symbol ?? ''
          this.monthlyIncomeShopV2 = `${totalReport.gross_amount} ${monthlyCurrency}`
        }
      } catch (e) {
        console.warn(`Finance Shop V2 API failed.`)
        console.log(e)
      } finally {
        this.initialLoadShopV2 = false
        this.loadingShopV2 = false
      }
    },
    async fetchKeyMetricsShopV2() {
      try {
        if (this.$store.getters.arena) {
          const now = new Date()
          const businessDate = new Date(now.getTime() - 6 * 60 * 60 * 1000)
          const year = businessDate.getFullYear()
          const month = businessDate.getMonth()
          const dateGte = `${year}-${String(month + 1).padStart(2, '0')}-01`
          const nextMonth = month === 11 ? 0 : month + 1
          const nextYear = month === 11 ? year + 1 : year
          const dateLt = `${nextYear}-${String(nextMonth + 1).padStart(
            2,
            '0',
          )}-01`

          const response = await ReportApi.report(
            this.$store.getters.arena.id,
            dateGte,
            dateLt,
          )

          const todayIndex = businessDate.getDate() - 1
          const todayReport = response.data.step_reports[todayIndex]
          const todayCurrency = todayReport?.report?.currency_code ?? ''
          const todayAmount = todayReport?.report?.gross_amount ?? 0
          this.todaysIncomeShopV2 = `${todayAmount} ${todayCurrency}`

          const monthlyCurrency = response.data.total_report.currency_code ?? ''
          const monthlyAmount = response.data.total_report.gross_amount
          this.monthlyIncomeShopV2 = `${monthlyAmount} ${monthlyCurrency}`
        }
      } catch (e) {
        console.warn(`Key Metrics Shop V2 API failed.`)
        console.log(e)
      }
    },
    async generateReport(type = 'monthly', date = null, exportFormat = 'pdf') {
      if (!date) {
        date = `${this.date}-01`
      }
      try {
        this.loadingIndexes.push(date)
        this.loadingFinancialReport = true
        const arena = this.$store.getters.arena
        if (arena) {
          await DashboardApi.generateReport({
            responseType: 'blob',
            params: {
              arena: JSON.stringify({
                id: arena.id,
                name: arena.name,
                timezone: arena.timezone,
                currency: arena.currency,
                coins_per_currency: arena.coins_per_currency,
              }),
              date: date,
              type: type,
              by_user: this.user.name,
              export_format: exportFormat,
            },
          })
            .then((response) => {
              const url = window.URL.createObjectURL(new Blob([response.data]))
              const link = document.createElement('a')
              link.href = url
              link.setAttribute(
                'download',
                `${arena.name.replaceAll(' ', '_')}_${date.replaceAll(
                  '-',
                  '_',
                )}_Financial_Report.${exportFormat}`,
              )
              document.body.appendChild(link)
              link.click()
            })
            .catch((error) => {
              this.axiosCatch(error)
            })
        }
      } catch (e) {
        console.warn(`Generate Report API failed.`)
        console.log(e)
      } finally {
        this.loadingFinancialReport = false
        this.loadingIndexes = this.loadingIndexes.filter((i) => i !== date)
      }
    },
    async generateReportShopV2(
      type = 'monthly',
      date = null,
      exportFormat = 'pdf',
      dayType = 'business',
    ) {
      if (!date) {
        date = `${this.dateShopV2}-01`
      }
      const dateGte = date
      const dateLimit = new Date(date)
      if (type === 'daily') {
        dateLimit.setUTCDate(dateLimit.getUTCDate() + 1)
      } else {
        dateLimit.setUTCMonth(dateLimit.getUTCMonth() + 1)
      }
      const dateLt = dateLimit.toISOString().substr(0, 10)
      try {
        this.loadingIndexesShopV2.push(date)
        this.loadingFinancialReportShopV2 = true
        const arena = this.$store.getters.arena
        if (arena) {
          await ReportApi.variantsReport(
            arena.id,
            exportFormat,
            dateGte,
            dateLt,
            dayType,
          )
            .then((response) => {
              const url = window.URL.createObjectURL(new Blob([response.data]))
              const link = document.createElement('a')
              link.href = url
              link.setAttribute(
                'download',
                `${arena.name.replaceAll(' ', '_')}_${date.replaceAll(
                  '-',
                  '_',
                )}_Financial_Report.${exportFormat}`,
              )
              document.body.appendChild(link)
              link.click()
            })
            .catch((error) => {
              this.axiosCatch(error)
            })
        }
      } catch (e) {
        console.warn(`Generate Report Shop V2 API failed.`)
        console.log(e)
      } finally {
        this.loadingFinancialReportShopV2 = false
        this.loadingIndexesShopV2 = this.loadingIndexesShopV2.filter(
          (i) => i !== date,
        )
      }
    },
    save() {
      this.$refs.dialog.save(this.date)
      this.fetchDailyFinance()
    },
    saveShopV2() {
      if (this.$refs.dialogShopV2) {
        this.$refs.dialogShopV2.save(this.dateShopV2)
      } else if (this.$refs.dialogShopV2Mobile) {
        this.$refs.dialogShopV2Mobile.save(this.dateShopV2)
      }
      this.fetchDailyFinanceShopV2()
    },
    refresh() {
      this.dateString = this.formatDate(new Date(), true, true)
      if (this.showShopV1) {
        this.fetchDailyFinance()
      }
      this.fetchDailyFinanceShopV2()
      this.fetchKeyMetricsShopV2()
    },
    reformatDate(dateStr) {
      const d = this.parseLocalDate(dateStr)
      return this.formatDate(d, false, true, true)
    },

    formatDateString(date) {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        '0',
      )}-${String(date.getDate()).padStart(2, '0')}`
    },

    parseLocalDate(dateString) {
      const [year, month, day] = dateString.split('-').map(Number)
      return new Date(year, month - 1, day)
    },
  },
})
</script>

<style>
.v-picker__title__btn {
  text-transform: capitalize !important;
}
</style>
